import router from '../router';

/**
 * Checks if a query value is valid.
 *
 * @param {any} value - The value to check.
 * @returns {boolean} - Whether the value is valid.
 */
const isValidQueryValue = (value) =>
  value !== '' && value !== null && !(Array.isArray(value) && value.length === 0);

/**
 * Parses a number value from a query and returns the appropriate data type.
 *
 * @param {string} value - The number value to parse.
 * @returns {number|bigint} - The parsed number value.
 */
const parseNumberValue = (value) =>
  !isNaN(value) ? (Number.isSafeInteger(Number(value)) ? Number(value) : BigInt(value)) : value;

/**
 * Checks if a value is a valid BigInt value.
 *
 * @param {any} value - The value to check.
 * @returns {boolean} - Whether the value is a valid BigInt value.
 */
const isBigIntValue = (value) => /^\d/.test(value) && !Number.isSafeInteger(Number(value));
/**
 * Formats the data into Vue Router query string format.
 *
 * @param {Object} data - The data to be formatted.
 * @returns {Object} - The formatted queries.
 */
export const formatToRouterQuery = (data) =>
  Object.fromEntries(
    Object.entries(data)
      .filter(([_, value]) => isValidQueryValue(value))
      .map(([key, value]) => [
        key,
        Array.isArray(value) ? value.map(String).join(',') : String(value),
      ]),
  );

/**
 * Reverses the Vue Router query string into the original data format.
 *
 * @returns {Object} - The reversed data.
 */
export const reverseQueryMapping = () =>
  Object.fromEntries(
    [...new URLSearchParams(router.currentRoute.value.query)]
      .filter(([_, value]) => value !== '')
      .map(([key, value]) => {
        const queryValue = value.includes(',') ? value.split(',') : value;

        if (!Array.isArray(queryValue)) {
          return [key, isBigIntValue(queryValue) ? queryValue : parseNumberValue(queryValue)];
        }

        const arrayValues = queryValue.map((val) =>
          isBigIntValue(val) ? val : parseNumberValue(val),
        );

        return [key, arrayValues];
      }),
  );

/**
 * Formats query params into Arrays, according to received list.
 * Leaves the rest of query params intact.
 *
 * @param {Array} multiValueFilters - List of query params to be "arrayized".
 * @returns {Array} - Array of queries.
 */
export const getFormattedRouteQuery = (multiValueFilters) => {
  const routeQuery = reverseQueryMapping();

  multiValueFilters.forEach((key) => {
    if (routeQuery[key] && !Array.isArray(routeQuery[key])) routeQuery[key] = [routeQuery[key]];
  });
  return routeQuery;
};

/**
 * Handles the queries by replacing them in the Vue Router.
 *
 * @param {Object} data - The data containing the queries.
 */
export const handleQueries = ({
  path = '',
  query = {},
  params = {},
  hash = '',
  replace = true,
  keepQuery = true,
}) => {
  router.push({
    ...(path && { path }),
    query: formatToRouterQuery({
      ...(keepQuery ? router.currentRoute.value.query : {}),
      ...query,
    }),
    params,
    replace,
    hash,
  });

  // @ToDo: Handle cross-domain communication.
};
